import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { Reader } from '@thiememeulenhoff/types';
import { ReadersService } from '@thiememeulenhoff/ng-api-gateway';
import { withRequestStatus } from '../_utils/request-status.feature';

type ReadersState = {
  readers: Array<Reader>;
};

const initialState: ReadersState = {
  readers: [],
};

export const ReadersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withMethods((store, readersService = inject(ReadersService)) => {
    const fetchReadersByMethodCode = rxMethod<string | undefined>(
      pipe(
        tap(() => patchState(store, { requestStatus: 'loading' })),
        switchMap((methodCode) =>
          (methodCode === undefined
            ? readersService.fetchReaders()
            : readersService.fetchReaders(methodCode)
          ).pipe(
            tapResponse({
              next: (readers) =>
                patchState(store, { readers, requestStatus: 'success' }),
              error: (error: Error) =>
                patchState(store, {
                  requestStatus: { error: error.message || 'Failed to fetch' },
                }),
            }),
          ),
        ),
      ),
    );

    return {
      fetchReaders: (methodCode?: string) =>
        fetchReadersByMethodCode(methodCode),
      fetchReader: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              requestStatus: 'loading',
              readers: store.readers(),
            }),
          ),
          switchMap((readerId) =>
            readersService.fetchReader(readerId).pipe(
              tapResponse({
                next: (reader) =>
                  patchState(store, {
                    requestStatus: 'success',
                    readers: [
                      ...store.readers().filter((r) => r.id !== reader.id),
                      reader,
                    ],
                  }),
                error: (error: Error) =>
                  patchState(store, {
                    requestStatus: {
                      error: error.message || 'Failed to fetch',
                    },
                  }),
              }),
            ),
          ),
        ),
      ),
    };
  }),
);
